/**
 * SEARCH-002 §6.1 — État d'ouverture de la palette ⌘K + raccourci clavier global.
 *
 * Store singleton module-level (useSyncExternalStore) : l'état est PARTAGÉ entre
 * tous les consommateurs (bouton barre, icône mobile, dialog) et le listener
 * `keydown` est binté UNE SEULE FOIS (corrige le re-bind à chaque toggle de
 * `useHelp`). ⌘K libre (Help = ⌘/). AZERTY-safe via `e.code === 'KeyK'`.
 */

import { useCallback, useSyncExternalStore } from 'react'

let isOpenState = false
const listeners = new Set<() => void>()

function emit() {
    for (const l of listeners) l()
}

function setOpen(next: boolean) {
    if (next !== isOpenState) {
        isOpenState = next
        emit()
    }
}

function subscribe(cb: () => void) {
    listeners.add(cb)
    return () => listeners.delete(cb)
}

// Listener global binté une seule fois (au premier usage du hook).
let keydownBound = false
function ensureKeydownListener() {
    if (keydownBound || typeof document === 'undefined') return
    keydownBound = true
    // CAPTURE phase + `stopPropagation` : ⌘K est RÉSERVÉ à la recherche globale.
    // En captant avant la cible, aucun champ (recherche de table, etc.) ne peut
    // « voler » le raccourci pour focus son propre input (SEARCH-002).
    document.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
            // AZERTY-safe : `e.code === 'KeyK'` couvre la position physique de la touche.
            const isCmdK =
                (event.metaKey || event.ctrlKey) && (event.key === 'k' || event.code === 'KeyK')
            if (isCmdK) {
                event.preventDefault()
                event.stopPropagation()
                setOpen(!isOpenState)
            }
        },
        true,
    )
}

export interface UseGlobalSearchReturn {
    isOpen: boolean
    open: () => void
    close: () => void
    toggle: () => void
}

export function useGlobalSearch(): UseGlobalSearchReturn {
    ensureKeydownListener()
    const isOpen = useSyncExternalStore(
        subscribe,
        () => isOpenState,
        () => false,
    )
    return {
        isOpen,
        open: useCallback(() => setOpen(true), []),
        close: useCallback(() => setOpen(false), []),
        toggle: useCallback(() => setOpen(!isOpenState), []),
    }
}
